import { useMemo, useRef, useState } from 'react';
import { ArrowRight, ArrowUpDown, FileText } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DatePicker, civilFromInstant, instantFromCivil, daysInMonth } from '@/components/ui/date-picker';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { toast } from '@/stores/toast-store';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { useLocationStore } from '@/stores/location-store';
import { useReportsEnabled } from '@/hooks/use-reports-access';
import { usePermissions } from '@/hooks/use-permissions';
import { cashNet, formatMDY, NOTE_VARIANT, systemNote } from '@/lib/cash-reconciliation';
import {
  usePosCashReconciliations,
  useSaveCashReconciliation,
  type PosCashReconciliationRow,
} from '@/hooks/use-pos-reports';

// ── Derived helpers ─────────────────────────────────────────────────────────
// cashNet / systemNote / NOTE_VARIANT / formatMDY live in @/lib/cash-reconciliation,
// shared with Reports → POS → Tills → Reconciled Cash Records.

// Resolve the day in the *reporting* zone (the zone the DatePicker renders in),
// not the browser's — otherwise the picked/default day is read a day off when the
// browser is ahead of the reporting zone (e.g. IST vs Pacific).
const toYmd = (d: Date, tz: string) => {
  const c = civilFromInstant(d, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}`;
};

function currentMonthRange(tz: string) {
  const t = civilFromInstant(new Date(), tz);
  return {
    from: instantFromCivil({ year: t.year, month: t.month, day: 1, hour: 0, minute: 0, second: 0 }, tz),
    to: instantFromCivil(
      { year: t.year, month: t.month, day: daysInMonth(t.year, t.month), hour: 0, minute: 0, second: 0 },
      tz,
    ),
  };
}

const rowKey = (r: PosCashReconciliationRow) => `${r.date}|${r.registerId ?? ''}`;

// ── Sorting (on the server values, so rows don't jump while editing) ─────────
type SortCol = 'date' | 'registerName' | 'employeeName' | 'systemReported' | 'actualReported' | 'net';

function compareRows(a: PosCashReconciliationRow, b: PosCashReconciliationRow, col: SortCol): number {
  switch (col) {
    case 'date':
      return a.date.localeCompare(b.date);
    case 'registerName':
      return a.registerName.localeCompare(b.registerName);
    case 'employeeName':
      return (a.employeeName ?? '').localeCompare(b.employeeName ?? '');
    case 'systemReported':
      return a.systemReported - b.systemReported;
    case 'actualReported':
      return (a.actualReported ?? -Infinity) - (b.actualReported ?? -Infinity);
    case 'net': {
      const na = a.actualReported == null ? -Infinity : a.actualReported - a.systemReported;
      const nb = b.actualReported == null ? -Infinity : b.actualReported - b.systemReported;
      return na - nb;
    }
  }
}

// ── Panel ───────────────────────────────────────────────────────────────────
export function CashReconciliationPanel() {
  const { selectedLocationId } = useLocationStore();
  // The zone the DatePicker renders in; the date helpers must use the same one.
  const { timeZone: reportTz } = useReportingTimeZone();
  const defaults = useMemo(() => currentMonthRange(reportTz), [reportTz]);

  const [fromInput, setFromInput] = useState<Date | null>(defaults.from);
  const [toInput, setToInput] = useState<Date | null>(defaults.to);
  const [applied, setApplied] = useState<{ from: Date | null; to: Date | null }>(defaults);
  const [sortCol, setSortCol] = useState<SortCol>('date');
  // Default to most-recent-first: date descending.
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Local overlay of in-flight edits, keyed by row. Display prefers the edit.
  const [edits, setEdits] = useState<Record<string, { actual?: string; notes?: string }>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  // Last value persisted per key, to skip redundant blur-saves.
  const lastSaved = useRef<Map<string, string>>(new Map());

  const params = {
    dateFrom: applied.from ? toYmd(applied.from, reportTz) : undefined,
    dateTo: applied.to ? toYmd(applied.to, reportTz) : undefined,
    locationId: selectedLocationId || undefined,
  };
  const { data, isLoading } = usePosCashReconciliations(params);
  const saveMutation = useSaveCashReconciliation();
  const serverRows = data?.data ?? [];

  const sorted = useMemo(() => {
    const rows = [...serverRows];
    rows.sort((a, b) => {
      const diff = compareRows(a, b, sortCol);
      return sortDir === 'asc' ? diff : -diff;
    });
    return rows;
  }, [serverRows, sortCol, sortDir]);

  const totalRows = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const visible = sorted.slice((page - 1) * perPage, page * perPage);

  const fromInvalid = !!fromInput && !!toInput && fromInput > toInput;

  const handleSearch = () => {
    setApplied({ from: fromInput, to: toInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = currentMonthRange(reportTz);
    setFromInput(r.from);
    setToInput(r.to);
    setApplied(r);
    setSortCol('date');
    setSortDir('desc');
    setPage(1);
  };
  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const displayActual = (r: PosCashReconciliationRow) => {
    const k = rowKey(r);
    return edits[k]?.actual ?? (r.actualReported != null ? String(r.actualReported) : '');
  };
  const displayNotes = (r: PosCashReconciliationRow) => {
    const k = rowKey(r);
    return edits[k]?.notes ?? r.bookKeepingNotes ?? '';
  };

  const setEdit = (k: string, patch: { actual?: string; notes?: string }) =>
    setEdits((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));

  // Entering actual cash and notes is its own key; without it the rows are read-only.
  const permissions = usePermissions();
  const canEdit = permissions.has('reconciliation.cash.edit');

  // Persist a row on blur. Requires a real register and a numeric amount.
  const commit = (r: PosCashReconciliationRow) => {
    const k = rowKey(r);
    if (!canEdit || !r.registerId || !selectedLocationId) return;
    const actualRaw = displayActual(r).trim();
    const actualNum = parseFloat(actualRaw);
    if (actualRaw === '' || Number.isNaN(actualNum)) return; // need a number to persist
    const notes = displayNotes(r);
    const signature = `${actualNum.toFixed(2)}|${notes}`;
    if (lastSaved.current.get(k) === signature) return; // nothing changed since last save
    // Skip if identical to server and never edited.
    if (!edits[k] && r.actualReported != null && r.actualReported === actualNum && (r.bookKeepingNotes ?? '') === notes)
      return;

    setSavingKeys((prev) => new Set(prev).add(k));
    saveMutation.mutate(
      {
        locationId: selectedLocationId,
        date: r.date,
        registerId: r.registerId,
        registerName: r.registerName,
        employeeId: r.employeeId,
        employeeName: r.employeeName,
        systemReported: r.systemReported,
        actualReported: actualNum,
        bookKeepingNotes: notes || null,
      },
      {
        onSuccess: () => {
          lastSaved.current.set(k, signature);
          toast.success('Saved', 1200);
        },
        onError: () => toast.error('Failed to save reconciliation'),
        onSettled: () =>
          setSavingKeys((prev) => {
            const n = new Set(prev);
            n.delete(k);
            return n;
          }),
      },
    );
  };

  const noLocation = !selectedLocationId;

  // The link lands on POS reports, so only offer it to people who can view
  // them with Reports switched on.
  const canOpenReports = useReportsEnabled() && permissions.has('reports.pos.view');
  // Open the report on the range applied here, so both pages show the same rows.
  const openCashRecords = () => {
    const q = new URLSearchParams({ tab: 'pos', report: 'cash-records' });
    if (applied.from && applied.to) {
      q.set('from', `${toYmd(applied.from, reportTz)}T00:00`);
      q.set('to', `${toYmd(applied.to, reportTz)}T23:59`);
    }
    window.location.hash = `/reports?${q.toString()}`;
  };

  return (
    <div className="space-y-4">
      {/* Link to the full report, called out on its own rather than sitting in
          the filter row where it read as just another filter control. The
          label matches the report's title so users know where they'll land. */}
      {canOpenReports && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Reconciled Cash Records</p>
            <p className="text-xs text-muted-foreground">
              See every drawer count for this date range in Reports, with totals and export.
            </p>
          </div>
          <Button size="sm" variant="primary" onClick={openCashRecords}>
            View Reconciled Cash Records
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Filter card */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Defaults to the current month</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Start Date</label>
            <DatePicker
              mode="date"
              timezone={reportTz}
              className="w-48"
              value={fromInput}
              onChange={(v) => setFromInput(v as Date | null)}
              maxDate={toInput ?? undefined}
              clearable={false}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">End Date</label>
            <DatePicker
              mode="date"
              timezone={reportTz}
              className="w-48"
              value={toInput}
              onChange={(v) => setToInput(v as Date | null)}
              minDate={fromInput ?? undefined}
              clearable={false}
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleSearch} disabled={fromInvalid}>
            Apply
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
        {fromInvalid && <p className="mt-2 text-xs text-red-600">Start Date must be on or before End Date.</p>}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <SortableHeader label="Date" col="date" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader
                  label="Register"
                  col="registerName"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Employee"
                  col="employeeName"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Cash - System Reported"
                  col="systemReported"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align="right"
                />
                <SortableHeader
                  label="Cash - Actually Reported"
                  col="actualReported"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align="right"
                />
                <SortableHeader
                  label="Net"
                  col="net"
                  sortCol={sortCol}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  align="right"
                />
                <th className="px-4 py-3 font-medium">System Notes</th>
                <th className="px-4 py-3 font-medium">Book Keeping Notes</th>
              </tr>
            </thead>
            <tbody>
              {noLocation ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                    Select a location to view cash reconciliation.
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                    No cash sales for the selected date range.
                  </td>
                </tr>
              ) : (
                visible.map((r) => {
                  const k = rowKey(r);
                  const actualRaw = displayActual(r);
                  const actualNum = parseFloat(actualRaw);
                  const hasActual = actualRaw.trim() !== '' && !Number.isNaN(actualNum);
                  const netValue = hasActual ? cashNet(actualNum, r.systemReported) : null;
                  const note = netValue == null ? null : systemNote(netValue);
                  const netCls =
                    netValue == null
                      ? 'text-muted-foreground'
                      : netValue < 0
                        ? 'text-red-600'
                        : netValue > 0
                          ? 'text-green-600'
                          : 'text-muted-foreground';
                  const editable = canEdit && !!r.registerId;
                  return (
                    <tr key={k} className="border-b border-border/60 last:border-b-0">
                      <td className="whitespace-nowrap px-4 py-3">{formatMDY(r.date)}</td>
                      <td className="px-4 py-3">{r.registerName}</td>
                      <td className="px-4 py-3">
                        <div>{r.employeeName ?? '—'}</div>
                        {r.employeeId && (
                          <div className="font-mono text-xs text-muted-foreground">{r.employeeId.slice(0, 8)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(r.systemReported)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block">
                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            $
                          </span>
                          <Input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            disabled={!editable || savingKeys.has(k)}
                            value={actualRaw}
                            onChange={(e) => setEdit(k, { actual: e.target.value })}
                            onBlur={() => commit(r)}
                            placeholder="0.00"
                            className="h-8 w-28 pl-5 text-right font-mono"
                          />
                        </div>
                      </td>
                      <td className={cn('px-4 py-3 text-right font-mono font-medium', netCls)}>
                        {netValue == null ? '—' : formatCurrency(netValue)}
                      </td>
                      <td className="px-4 py-3">{note ? <Badge variant={NOTE_VARIANT[note]}>{note}</Badge> : '—'}</td>
                      <td className="px-4 py-3">
                        <Input
                          type="text"
                          disabled={!editable || savingKeys.has(k)}
                          value={displayNotes(r)}
                          onChange={(e) => setEdit(k, { notes: e.target.value })}
                          onBlur={() => commit(r)}
                          placeholder="Add note…"
                          className="h-8 w-48"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalRows > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={totalRows}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SortableHeader({
  label,
  col,
  sortCol,
  sortDir,
  onSort,
  align = 'left',
}: {
  label: string;
  col: SortCol;
  sortCol: SortCol;
  sortDir: 'asc' | 'desc';
  onSort: (col: SortCol) => void;
  align?: 'left' | 'right';
}) {
  const active = sortCol === col;
  return (
    <th className={cn('px-4 py-3 font-medium', align === 'right' && 'text-right')}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={cn(
          'inline-flex items-center gap-1 uppercase transition-colors hover:text-foreground',
          align === 'right' && 'flex-row-reverse',
          active && 'text-foreground',
        )}
      >
        {label}
        <ArrowUpDown
          className={cn(
            'h-3 w-3',
            active ? 'text-primary' : 'text-muted-foreground/60',
            active && sortDir === 'desc' && 'rotate-180',
          )}
        />
      </button>
    </th>
  );
}
